import type { CartProductInterface } from '@/types'
import getProductKey from '@/utils/products/getProductKey'

import type { ProductQuantityInfo } from './cartServiceTypes'
import { cartValidationService } from './CartValidationService'

export class CartProductService {
  addOriginalIndex(products: CartProductInterface[]): CartProductInterface[] {
    return products.map((product, index) => ({
      ...product,
      originalIndex:
        product.originalIndex !== undefined ? product.originalIndex : index,
    }))
  }

  extractQuantities(products: CartProductInterface[]): Record<string, number> {
    const quantities: Record<string, number> = {}

    products.forEach((product) => {
      const stock = product.suppliersStock?.[0]?.stock?.[0]
      if (stock) {
        const quantity = stock.cartInfo?.quantity ?? stock.quantityInCart ?? 0
        quantities[String(getProductKey(product))] = quantity
      }
    })

    return quantities
  }

  updateCheckedProducts(
    prevChecked: CartProductInterface[],
    newProducts: CartProductInterface[]
  ): CartProductInterface[] {
    return prevChecked.filter((checkedProduct) => {
      const currentProduct = newProducts.find(
        (p) => getProductKey(p) === getProductKey(checkedProduct)
      )

      if (!currentProduct) {
        return false
      }

      return cartValidationService.isProductAvailable(currentProduct)
    })
  }

  getQuantityInfo(product: CartProductInterface): ProductQuantityInfo {
    const stockItem = product.suppliersStock?.[0]?.stock?.[0]
    const available = stockItem?.quantity ?? 0
    const inCart =
      stockItem?.cartInfo?.quantity ?? stockItem?.quantityInCart ?? 0
    const isProblematic =
      cartValidationService.isStatusUnavailable(product) ||
      available === 0 ||
      inCart > available ||
      inCart === 0

    return {
      available,
      inCart,
      isProblematic,
      originalIndex: product.originalIndex,
    }
  }

  detectResolvedProducts(
    prevProducts: CartProductInterface[],
    currentProducts: CartProductInterface[]
  ): string | null {
    for (const currentProduct of currentProducts) {
      const currentKey = String(getProductKey(currentProduct))
      const prevProduct = prevProducts.find(
        (p) => String(getProductKey(p)) === currentKey
      )

      const wasProblematic = prevProduct
        ? cartValidationService.isProductProblematic(prevProduct)
        : false
      const isNowProblematic =
        cartValidationService.isProductProblematic(currentProduct)

      if (wasProblematic && !isNowProblematic) {
        return currentKey
      }
    }

    return null
  }

  getProductKey(product: CartProductInterface): string {
    return String(getProductKey(product))
  }
}

export const cartProductService = new CartProductService()
